import type { MissionAssessmentCoreSelectDto } from '@@types/api.types';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@components/ui/tabs';
import type { FunctionComponent } from 'react';
import { TechnologyMissionAssessmentEffectsComponent } from './technology-mission-assessment-effects.component';
import { TechnologyMissionAssessmentExpertNotesComponent } from './technology-mission-assessment-expert-notes.component';
import { TechnologyMissionAssessmentHurdlesComponent } from './technology-mission-assessment-hurdles.component';
import { TechnologyMissionAssessmentIntersComponent } from './technology-mission-assessment-inters.component';
import { TechnologyMissionAssessmentServicesComponent } from './technology-mission-assessment-services.component';

type Tab = 'all' | 'effects' | 'expertNotes' | 'hurdles' | 'interoperability' | 'services';

type TechnologyMissionAssessmentTabsProps = {
  missionAssessment?: MissionAssessmentCoreSelectDto;
  missionAssessmentId: string;
  onTabChange: (tab: Tab) => void;
  renderContentOnly?: boolean;
  selectedTab: Tab;
  technologyId: string;
};

type TabConfig = {
  component: FunctionComponent<{ missionAssessmentId: string; technologyId?: string }>;
  label: string;
  needsTechnologyId?: boolean;
};

const baseTabConfig: Record<Exclude<Tab, 'all'>, TabConfig> = {
  effects: {
    component: TechnologyMissionAssessmentEffectsComponent,
    label: 'Effects',
    needsTechnologyId: true,
  },
  expertNotes: {
    component: TechnologyMissionAssessmentExpertNotesComponent,
    label: 'Capability Assessment',
  },
  hurdles: {
    component: TechnologyMissionAssessmentHurdlesComponent,
    label: 'Technology Hurdles',
  },
  interoperability: {
    component: TechnologyMissionAssessmentIntersComponent,
    label: 'Platform Alignment',
  },
  services: {
    component: TechnologyMissionAssessmentServicesComponent,
    label: 'Services & Description',
  },
};

// Order of tabs for rendering
const tabOrder: Exclude<Tab, 'all'>[] = ['services', 'effects', 'interoperability', 'hurdles', 'expertNotes'];
const reportSectionOptions: Array<{ label: string; value: Tab }> = [
  { label: 'All Sections', value: 'all' },
  ...tabOrder.map((key) => ({
    label: baseTabConfig[key].label,
    value: key,
  })),
];

const isTab = (value: string): value is Tab => {
  return reportSectionOptions.some((option) => option.value === value);
};

export const TechnologyMissionAssessmentTabs: React.FC<TechnologyMissionAssessmentTabsProps> = (props) => {
  const {
    missionAssessment,
    missionAssessmentId,
    onTabChange,
    renderContentOnly = false,
    selectedTab,
    technologyId,
  } = props;

  const handleTabClick = (value: string): void => {
    if (!isTab(value)) {
      return;
    }

    onTabChange(value);
  };

  const renderTabContent = (config: TabConfig) => {
    const Component = config.component;
    return (
      <Component
        missionAssessmentId={missionAssessmentId}
        technologyId={config.needsTechnologyId ? technologyId : undefined}
      />
    );
  };

  const AllContent = (
    <div className="space-y-8">
      {tabOrder.map((key) => {
        const config = baseTabConfig[key];
        return (
          <div
            className="pt-2 first:pt-0"
            id={`section-${key}`}
            key={key}
          >
            <div className="text-gray-700 text-lg font-medium mb-2">{config.label}</div>
            {renderTabContent(config)}
            {key !== 'expertNotes' && <div className="border-t border-[#E1E1E1] mt-8" />}
          </div>
        );
      })}
    </div>
  );

  const selectedSectionLabel =
    reportSectionOptions.find((option) => option.value === selectedTab)?.label ?? 'All Sections';
  const HeaderSection = missionAssessment ? (
    <div className="flex flex-col gap-4 border-b border-gray-100 p-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-gray-500">
        <span>
          Assessed on{' '}
          {new Date(missionAssessment.createdAt).toLocaleDateString('en-US', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
          })}
        </span>
        <span className="text-gray-300">•</span>
        <span>{missionAssessment.request?.use?.name ?? 'Unknown Use'}</span>
        <span className="text-gray-300">•</span>
        <span>{missionAssessment.request?.domain?.name ?? 'Unknown Domain'}</span>
        {missionAssessment.expert?.profile?.fullName && (
          <>
            <span className="text-gray-300">•</span>
            <span>{missionAssessment.expert.profile.fullName}</span>
          </>
        )}
      </div>

      <Select
        onValueChange={handleTabClick}
        value={selectedTab}
      >
        <SelectTrigger className="min-w-[220px] bg-white sm:w-[220px]">
          <SelectValue placeholder="Filter sections">{selectedSectionLabel}</SelectValue>
        </SelectTrigger>
        <SelectContent>
          {reportSectionOptions.map((option) => (
            <SelectItem
              key={option.value}
              value={option.value}
            >
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  ) : null;

  if (renderContentOnly) {
    return (
      <>
        {HeaderSection}
        <div className="p-4">
          {selectedTab === 'all' ? (
            AllContent
          ) : (
            <>
              {tabOrder.map((key) => {
                if (selectedTab !== key) return null;
                const config = baseTabConfig[key];
                return (
                  <div
                    key={key}
                    id={`section-${key}`}
                  >
                    <div className="text-gray-700 text-lg font-medium mb-2">{config.label}</div>
                    {renderTabContent(config)}
                  </div>
                );
              })}
            </>
          )}
        </div>
      </>
    );
  }

  return (
    <Tabs
      className="w-full"
      onValueChange={handleTabClick}
      orientation="vertical"
      value={selectedTab}
    >
      <TabsList className="flex flex-col h-auto items-stretch bg-transparent p-0 gap-1">
        <TabsTrigger
          className="justify-start data-[state=active]:bg-[#3D2EFF1A] data-[state=active]:text-[#3D2EFF] data-[state=active]:border-transparent border-transparent hover:bg-gray-100"
          value="all"
        >
          All Sections
        </TabsTrigger>
        {tabOrder.map((key) => (
          <TabsTrigger
            className="justify-start data-[state=active]:bg-[#3D2EFF1A] data-[state=active]:text-[#3D2EFF] data-[state=active]:border-transparent border-transparent hover:bg-gray-100"
            key={key}
            value={key}
          >
            {baseTabConfig[key].label}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
};
